import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Save, X, Trash2, AlertTriangle, Copy, CheckCircle2, UserMinus, UserX, GraduationCap, Link2, ArchiveRestore, ChevronDown, ChevronUp, Users, BookOpen, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { ConfirmDialog } from '../../../components/shared/ConfirmDialog';
import { AddLearnerSheet } from '../components/AddLearnerSheet';
import { ClassroomTeachersTab } from '../components/ClassroomTeachersTab';
import { CoTeacherWelcomeModal } from '../components/CoTeacherWelcomeModal';
import { TransferClassroomModal } from '../components/TransferClassroomModal';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { DjayAvatar } from '../../../mascot';
import { getLearnerClassroomFlags } from '../utils/studentUtils';
import { type Classroom, type TeacherAccount, type ClassroomValidationWarning, generateClassroomId, validateNewClassroom, findDuplicateClassroomName, isLearnerAccount, findClassroomByJoinCode, getClassroomRole, MAX_CO_TEACHERS_PER_CLASSROOM } from '../../../accounts';
import { StudentData } from './StudentDetailPanel';
interface ManagementPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  selectedClassroomId?: string | null;
  students: StudentData[];
}
export function ManagementPage({
  activeAccount,
  classrooms = [],
  selectedClassroomId = null,
  students
}: ManagementPageProps) {
  const {
    accounts,
    addClassroom,
    updateClassroom,
    removeClassroom,
    removeLearnerFromClassroom,
    unlinkTeacherFromLearner,
    restoreArchivedLearner,
    joinClassroomAsCoTeacher,
    regenerateJoinCode
  } = useAppStore();
  const [isJoiningAsCoTeacher, setIsJoiningAsCoTeacher] = useState(false);
  const [coTeacherCode, setCoTeacherCode] = useState('');
  const [coTeacherJoinError, setCoTeacherJoinError] = useState<string | null>(null);
  const [coTeacherWelcomeFor, setCoTeacherWelcomeFor] = useState<Classroom | null>(null);
  const handleJoinAsCoTeacher = () => {
    setCoTeacherJoinError(null);
    const code = coTeacherCode.trim().toUpperCase();
    if (!code) {
      setCoTeacherJoinError('Vul eerst een code in.');
      return;
    }
    const target = findClassroomByJoinCode(accounts, code);
    if (!target) {
      setCoTeacherJoinError('Geen klas gevonden met deze code.');
      return;
    }
    const role = getClassroomRole(accounts, target.classroom.id, activeAccount.id);
    if (role === 'owner') {
      setCoTeacherJoinError('Je bent al de eigenaar van deze klas.');
      return;
    }
    if (role === 'co-teacher') {
      setCoTeacherJoinError('Je bent al co-docent in deze klas.');
      return;
    }
    const currentCoTeachers = target.classroom.coTeacherIds ?? [];
    if (currentCoTeachers.length >= MAX_CO_TEACHERS_PER_CLASSROOM) {
      setCoTeacherJoinError(`Deze klas heeft al ${MAX_CO_TEACHERS_PER_CLASSROOM} co-docenten. Vraag de eigenaar om iemand te verwijderen.`);
      return;
    }
    joinClassroomAsCoTeacher(activeAccount.id, target.classroom.id);
    setCoTeacherWelcomeFor(target.classroom);
    setIsJoiningAsCoTeacher(false);
    setCoTeacherCode('');
  };
  const [isEditingClass, setIsEditingClass] = useState<string | null>(null);
  const [editClassName, setEditClassName] = useState('');
  const [editGroupLevel, setEditGroupLevel] = useState(6);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [newClassName, setNewClassName] = useState('');
  const [newGroupLevel, setNewGroupLevel] = useState(6);
  const [classWarnings, setClassWarnings] = useState<ClassroomValidationWarning[]>([]);
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);
  const [deleteClassroomId, setDeleteClassroomId] = useState<string | null>(null);
  const [transferClassroomId, setTransferClassroomId] = useState<string | null>(null);
  const [regenerateCodeClassroomId, setRegenerateCodeClassroomId] = useState<string | null>(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [newClassroomId, setNewClassroomId] = useState<string | null>(null);
  const [pendingRemoveStudent, setPendingRemoveStudent] = useState<{
    studentId: string;
    studentName: string;
    classroomId: string;
    classroomName: string;
  } | null>(null);
  const [pendingUnlinkStudent, setPendingUnlinkStudent] = useState<{
    studentId: string;
    studentName: string;
  } | null>(null);
  const [showArchive, setShowArchive] = useState(false);
  const [activeTab, setActiveTab] = useState<'leerlingen' | 'klassen' | 'docenten' | 'archief'>('leerlingen');
  const handleSaveClass = (classroomId: string) => {
    if (editClassName.trim()) {
      const duplicates = findDuplicateClassroomName(accounts, editClassName.trim(), classroomId);
      if (duplicates.length > 0) {
        const dup = duplicates[0];
        const isSameTeacher = dup.classroom.teacherId === activeAccount.id;
        setClassWarnings([{
          type: 'duplicate_name',
          message: isSameTeacher ? `Je hebt al een klas met de naam "${dup.classroom.name}".` : `"${dup.classroom.name}" bestaat al bij ${dup.teacherName}.`,
          details: {
            classroomName: dup.classroom.name,
            classroomId: dup.classroom.id,
            teacherName: dup.teacherName,
            teacherId: dup.classroom.teacherId
          }
        }]);
        return;
      }
      updateClassroom(activeAccount.id, classroomId, {
        name: editClassName.trim(),
        groupLevel: editGroupLevel
      });
      setIsEditingClass(null);
      setClassWarnings([]);
    }
  };
  const handleAddClassroom = () => {
    if (!newClassName.trim()) return;
    const warnings = validateNewClassroom(accounts, newClassName.trim(), activeAccount.id);
    const hasSameTeacherDuplicate = warnings.some(w => w.details.teacherId === activeAccount.id);
    if (hasSameTeacherDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    if (warnings.length > 0 && !confirmDuplicate) {
      setClassWarnings(warnings);
      return;
    }
    const newClassroom: Classroom = {
      id: generateClassroomId(),
      name: newClassName.trim(),
      groupLevel: newGroupLevel,
      learnerIds: [],
      createdAt: Date.now(),
      teacherId: activeAccount.id,
      joinCode: '' // Auto-generated by addClassroom()
    };
    addClassroom(activeAccount.id, newClassroom);
    setIsAddingClass(false);
    setNewClassName('');
    setNewGroupLevel(6);
    setClassWarnings([]);
    setConfirmDuplicate(false);
    setNewClassroomId(newClassroom.id);
    setTimeout(() => setNewClassroomId(null), 3000);
  };
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  const activeClassroom = selectedClassroomId ? classrooms.find(c => c.id === selectedClassroomId) : null;
  const classStudents = (activeClassroom ? students.filter(s => activeClassroom.learnerIds.includes(s.id)) : students).sort((a, b) => a.name.localeCompare(b.name));
  const classroomToDelete = deleteClassroomId ? classrooms.find(c => c.id === deleteClassroomId) : null;
  const archivedLinks = accounts.relationships.archivedLearnerLinks?.[activeAccount.id] || [];
  const recentArchivedLinks = archivedLinks.filter(link => Date.now() - link.archivedAt < 30 * 24 * 60 * 60 * 1000).sort((a, b) => b.archivedAt - a.archivedAt);
  return <PageLayout title="Klas Beheer" description="Beheer je klassen en leerlingen" data-id="element-8699">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-8700">
        {/* Segmented Tabs */}
        <div className="sticky top-0 z-10 bg-slate-50/80 backdrop-blur-md p-1.5 rounded-xl border border-slate-200 flex items-center gap-1 overflow-x-auto custom-scrollbar shadow-sm" data-id="element-8701">
          <button onClick={() => setActiveTab('leerlingen')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'leerlingen' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-8702">
            <Users className="w-4 h-4" data-id="element-8703" />
            Leerlingen
          </button>
          <button onClick={() => setActiveTab('klassen')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'klassen' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-8704">
            <BookOpen className="w-4 h-4" data-id="element-8705" />
            Klassen
          </button>
          <button onClick={() => setActiveTab('docenten')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'docenten' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-8706">
            <UserCheck className="w-4 h-4" data-id="element-8707" />
            Docenten
          </button>
          {recentArchivedLinks.length > 0 && <button onClick={() => setActiveTab('archief')} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${activeTab === 'archief' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-8708">
              <ArchiveRestore className="w-4 h-4" data-id="element-8709" />
              Archief ({recentArchivedLinks.length})
            </button>}
        </div>

        {/* Students Management Section */}
        {activeTab === 'leerlingen' && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-8710">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-8711">
              <div data-id="element-8712">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-8713">
                  Leerlingen Beheer
                </h3>
                <p className="text-xs text-slate-400 mt-0.5" data-id="element-8714">
                  {activeClassroom ? `${activeClassroom.name} (${classStudents.length} leerlingen)` : `Alle gekoppelde leerlingen (${classStudents.length})`}
                </p>
              </div>
              <Button onClick={() => setShowAddStudent(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-8715">
                <Plus className="w-4 h-4" data-id="element-8716" />
                Leerling Toevoegen
              </Button>
            </div>

            <div className="p-4" data-id="element-8717">
              {classStudents.length === 0 ? <div className="text-center py-8 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-8718">
                  <div className="mb-3 flex justify-center" data-id="element-8719">
                    <DjayAvatar size="md" emotion="surprised" data-id="element-8720" />
                  </div>
                  <p className="text-slate-500 font-medium" data-id="element-8721">
                    {activeClassroom ? `Nog geen leerlingen in ${activeClassroom.name}.` : 'Je hebt nog geen leerlingen gekoppeld.'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1" data-id="element-8722">
                    {activeClassroom ? 'Voeg leerlingen toe via de knop hierboven.' : 'Selecteer een klas in de zijbalk of voeg leerlingen toe.'}
                  </p>
                </div> : <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-8723">
                  <div className="flex items-center justify-between p-4 bg-slate-50 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-8724">
                    <div data-id="element-8725">Leerling</div>
                    <div data-id="element-8726">Acties</div>
                  </div>
                  {classStudents.map((student, i) => {
              const initial = student.name.charAt(0).toUpperCase();
              const flags = getLearnerClassroomFlags(accounts, student.id, activeClassroom || null);
              const learnerAccount = accounts.accountsById[student.id];
              const learnerGrade = learnerAccount && isLearnerAccount(learnerAccount) ? learnerAccount.grade : undefined;
              return <div key={student.id} className={`flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`} data-id="element-8727">
                        <div className="flex items-center gap-3 flex-wrap" data-id="element-8728">
                          <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-sm shadow-sm" data-id="element-8729">
                            {initial}
                          </div>
                          <div className="font-bold text-sm text-slate-900" data-id="element-8730">
                            {student.name}
                          </div>
                          {flags.gradeMismatch && learnerGrade && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-md" title={`Werkt op groep-${learnerGrade}-niveau (deze klas is groep ${activeClassroom?.groupLevel}).`} data-id="element-8731">
                              <GraduationCap className="w-3 h-3" data-id="element-8732" />
                              Gr {learnerGrade}
                            </span>}
                          {flags.gradeUnknown && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-bold rounded-md" title="Deze leerling heeft nog geen groep ingesteld." data-id="element-8733">
                              <GraduationCap className="w-3 h-3" data-id="element-8734" />
                              Geen groep
                            </span>}
                          {(flags.multiClassroom || flags.multiTeacher) && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sky-100 text-sky-700 text-xs font-bold rounded-md" title={flags.otherEnrollments.length > 0 ? `Ook in: ${flags.otherEnrollments.map(e => e.classroomName + ' (' + e.teacherName + ')').join(', ')}` : 'Gekoppeld aan meer dan een leerkracht'} data-id="element-8735">
                              <Link2 className="w-3 h-3" data-id="element-8736" />
                              {flags.otherEnrollments.length > 0 ? '+' + flags.otherEnrollments.length + (flags.otherEnrollments.length === 1 ? ' klas' : ' klassen') : 'Meerdere koppelingen'}
                            </span>}
                          {!activeClassroom && !flags.gradeMismatch && !flags.gradeUnknown && learnerGrade && <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 text-xs font-bold rounded" data-id="element-8737">
                                Gr {learnerGrade}
                              </span>}
                        </div>
                        <div className="flex items-center gap-2" data-id="element-8738">
                          {activeClassroom ? <>
                              <button onClick={() => {
                      setPendingRemoveStudent({
                        studentId: student.id,
                        studentName: student.name,
                        classroomId: activeClassroom.id,
                        classroomName: activeClassroom.name
                      });
                    }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors group" title="Verwijder uit klas" data-id="element-8739">
                                <UserX className="w-4 h-4" data-id="element-8740" />
                                <span className="hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-8741">
                                  Verwijder uit klas
                                </span>
                              </button>
                              {getClassroomRole(accounts, activeClassroom.id, activeAccount.id) === 'owner' && <>
                                  <div className="w-px h-4 bg-slate-200 mx-1" data-id="element-8742"></div>
                                  <button onClick={() => {
                        setPendingUnlinkStudent({
                          studentId: student.id,
                          studentName: student.name
                        });
                      }} className="p-1.5 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" title="Ontkoppel volledig van jouw account" data-id="element-8743">
                                    <UserMinus className="w-4 h-4" data-id="element-8744" />
                                  </button>
                                </>}
                            </> : <button onClick={() => {
                    setPendingUnlinkStudent({
                      studentId: student.id,
                      studentName: student.name
                    });
                  }} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors group" title="Ontkoppel volledig van jouw account" data-id="element-8745">
                              <UserMinus className="w-4 h-4" data-id="element-8746" />
                              <span className="hidden sm:inline opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-8747">
                                Ontkoppel
                              </span>
                            </button>}
                        </div>
                      </div>;
            })}
                </div>}
            </div>
          </Card>}

        {/* Classes Section */}
        {activeTab === 'klassen' && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-8748">
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-8749">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-8750">
                Mijn Klassen
              </h3>
              <div className="flex items-center gap-2" data-id="element-8751">
                <Button onClick={() => setIsAddingClass(true)} variant="primary" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-8752">
                  <Plus className="w-4 h-4" data-id="element-8753" />
                  Nieuwe Klas
                </Button>
              </div>
            </div>

            <div className="p-4 space-y-4" data-id="element-8754">
              {isAddingClass && <div className="p-5 bg-indigo-50/50 border border-indigo-100 rounded-xl" data-id="element-8755">
                  <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-8756">
                    Nieuwe klas toevoegen
                  </h4>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-8757">
                    <div className="flex-1 w-full" data-id="element-8758">
                      <Input variant="admin" label="Klasnaam" value={newClassName} onChange={e => setNewClassName(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddClassroom();
                  }
                }} placeholder="Bijv. Groep 7B" autoFocus data-id="element-8759" />
                    </div>
                    <div className="w-full sm:w-32" data-id="element-8760">
                      <Select variant="admin" label="Niveau" value={newGroupLevel} onChange={e => setNewGroupLevel(parseInt(e.target.value))} options={[{
                  value: '6',
                  label: 'Groep 6'
                }, {
                  value: '7',
                  label: 'Groep 7'
                }, {
                  value: '8',
                  label: 'Groep 8'
                }]} data-id="element-8761" />
                    </div>
                    <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-5" data-id="element-8762">
                      <Button onClick={handleAddClassroom} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-8763">
                        <Save className="w-4 h-4" data-id="element-8764" />
                        Aanmaken
                      </Button>
                      <Button onClick={() => {
                  setIsAddingClass(false);
                  setClassWarnings([]);
                  setConfirmDuplicate(false);
                }} variant="secondary" data-id="element-8765">
                        Annuleren
                      </Button>
                    </div>
                  </div>
                  {classWarnings.length > 0 && <div className="mt-3 space-y-2" data-id="element-8766">
                      {classWarnings.map((w, i) => <div key={i} className={`flex items-start gap-2 p-3 rounded-lg text-sm ${w.details.teacherId === activeAccount.id ? 'bg-red-50 border border-red-200 text-red-800' : 'bg-amber-50 border border-amber-200 text-amber-800'}`} data-id="element-8767">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-8768" />
                          <div data-id="element-8769">
                            <p className="font-medium" data-id="element-8770">{w.message}</p>
                            {w.details.teacherId !== activeAccount.id && !confirmDuplicate && <button onClick={() => setConfirmDuplicate(true)} className="mt-1 text-xs font-bold underline hover:no-underline" data-id="element-8771">
                                  Ja, toch aanmaken
                                </button>}
                          </div>
                        </div>)}
                    </div>}
                </div>}

              {classrooms.length === 0 && !isAddingClass ? <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-8772">
                  <div className="mb-4 flex justify-center" data-id="element-8773">
                    <DjayAvatar size="md" emotion="thinking" data-id="element-8774" />
                  </div>
                  <p className="text-slate-500 mb-4 font-medium" data-id="element-8775">
                    Je hebt nog geen klassen aangemaakt.
                  </p>
                  <Button onClick={() => setIsAddingClass(true)} variant="outline" className="border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300" data-id="element-8776">
                    Maak je eerste klas aan
                  </Button>
                </div> : <div className="grid grid-cols-1 gap-3" data-id="element-8777">
                  {classrooms.map((classroom: Classroom) => <motion.div key={classroom.id} initial={newClassroomId === classroom.id ? {
              backgroundColor: '#f0fdf4',
              borderColor: '#86efac'
            } : {}} animate={{
              backgroundColor: '#ffffff',
              borderColor: '#e2e8f0'
            }} transition={{
              duration: 2,
              delay: 1
            }} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-white border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors shadow-sm" data-id="element-8778">
                      {isEditingClass === classroom.id ? <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full" data-id="element-8779">
                          <div className="flex-1 w-full" data-id="element-8780">
                            <Input variant="admin" value={editClassName} onChange={e => setEditClassName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveClass(classroom.id);
                    }
                  }} placeholder="Klasnaam" autoFocus data-id="element-8781" />
                          </div>
                          <div className="w-full sm:w-32" data-id="element-8782">
                            <Select variant="admin" value={editGroupLevel} onChange={e => setEditGroupLevel(parseInt(e.target.value))} options={[{
                    value: '6',
                    label: 'Groep 6'
                  }, {
                    value: '7',
                    label: 'Groep 7'
                  }, {
                    value: '8',
                    label: 'Groep 8'
                  }]} data-id="element-8783" />
                          </div>
                          <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-0" data-id="element-8784">
                            <Button onClick={() => handleSaveClass(classroom.id)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-8785">
                              <Save className="w-4 h-4" data-id="element-8786" />
                              Opslaan
                            </Button>
                            <Button onClick={() => {
                    setIsEditingClass(null);
                    setClassWarnings([]);
                  }} variant="secondary" size="sm" data-id="element-8787">
                              <X className="w-4 h-4" data-id="element-8788" />
                            </Button>
                          </div>
                        </div> : <>
                          <div className="flex items-center gap-4 mb-3 sm:mb-0" data-id="element-8789">
                            <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600 font-black text-lg border border-indigo-100" data-id="element-8790">
                              {classroom.groupLevel}
                            </div>
                            <div data-id="element-8791">
                              <h4 className="font-bold text-slate-900 text-base" data-id="element-8792">
                                {classroom.name}
                              </h4>
                              <div className="flex items-center gap-3 text-sm text-slate-500 font-medium mt-0.5" data-id="element-8793">
                                <span data-id="element-8794">
                                  {classroom.learnerIds.length} leerlingen
                                </span>
                                <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-8795" />
                                <span data-id="element-8796">
                                  {(classroom.coTeacherIds?.length || 0) + 1}{' '}
                                  docenten
                                </span>
                                <span className="w-1 h-1 rounded-full bg-slate-300" data-id="element-8797" />
                                <div className="flex items-center gap-1.5" data-id="element-8798">
                                  <span data-id="element-8799">Code:</span>
                                  <span className="font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded" data-id="element-8800">
                                    {classroom.joinCode}
                                  </span>
                                  {getClassroomRole(accounts, classroom.id, activeAccount.id) === 'owner' && <>
                                      <button onClick={() => handleCopyCode(classroom.joinCode)} className="text-slate-400 hover:text-indigo-600 transition-colors" title="Kopieer code" data-id="element-8801">
                                        {copiedCode === classroom.joinCode ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" data-id="element-8802" /> : <Copy className="w-3.5 h-3.5" data-id="element-8803" />}
                                      </button>
                                      <button onClick={() => setRegenerateCodeClassroomId(classroom.id)} className="text-xs text-indigo-600 hover:text-indigo-700 font-bold ml-1" data-id="element-8804">
                                        Nieuwe code
                                      </button>
                                    </>}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2" data-id="element-8805">
                            {newClassroomId === classroom.id && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md mr-2" data-id="element-8806">
                                Nieuw!
                              </span>}
                            {getClassroomRole(accounts, classroom.id, activeAccount.id) === 'owner' ? <>
                                <Button onClick={() => {
                      if (isEditingClass !== classroom.id) {
                        setClassWarnings([]);
                      }
                      setIsEditingClass(classroom.id);
                      setEditClassName(classroom.name);
                      setEditGroupLevel(classroom.groupLevel);
                    }} variant="ghost" size="sm" data-id="element-8807">
                                  <Edit2 className="w-4 h-4 mr-2" data-id="element-8808" /> Bewerk
                                </Button>
                                <Button onClick={() => setTransferClassroomId(classroom.id)} variant="ghost" size="sm" data-id="element-8809">
                                  Draag over...
                                </Button>
                                <Button onClick={() => setDeleteClassroomId(classroom.id)} variant="ghost" size="sm" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-id="element-8810">
                                  <Trash2 className="w-4 h-4" data-id="element-8811" />
                                </Button>
                              </> : <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-amber-100 text-amber-700 rounded" title="Je bent co-docent in deze klas — alleen de eigenaar kan klasinstellingen wijzigen" data-id="element-8812">
                                Co-docent
                              </span>}
                          </div>
                        </>}
                    </motion.div>)}
                </div>}
            </div>
          </Card>}

        {/* Teachers Management Section */}
        {activeTab === 'docenten' && <div className="space-y-6" data-id="element-8813">
            {activeClassroom ? <ClassroomTeachersTab classroom={activeClassroom} activeAccount={activeAccount} data-id="element-8814" /> : <div className="text-center py-10 bg-slate-50 rounded-xl border border-slate-200 border-dashed" data-id="element-8815">
                <div className="mb-4 flex justify-center" data-id="element-8816">
                  <DjayAvatar size="md" emotion="thinking" data-id="element-8817" />
                </div>
                <p className="text-slate-500 font-medium" data-id="element-8818">
                  Selecteer eerst een klas in de zijbalk om co-docenten te
                  beheren.
                </p>
              </div>}

            {/* Join as Co-Teacher Section */}
            <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-8819">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100" data-id="element-8820">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-8821">
                  Toetreden tot een klas
                </h3>
              </div>
              <div className="p-4" data-id="element-8822">
                {!isJoiningAsCoTeacher ? <Button onClick={() => {
              setIsJoiningAsCoTeacher(true);
              setCoTeacherJoinError(null);
            }} variant="secondary" className="w-full sm:w-auto" data-id="element-8823">
                    Ik heb een co-docent-code
                  </Button> : <div className="p-4 bg-amber-50/60 border border-amber-100 rounded-xl" data-id="element-8824">
                    <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-8825">
                      Ik heb een co-docent-code
                    </h4>
                    <p className="text-xs text-slate-600 mb-3" data-id="element-8826">
                      Vraag de klas-eigenaar om de klascode en voer hem hier in.
                    </p>
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3" data-id="element-8827">
                      <div className="flex-1 w-full" data-id="element-8828">
                        <Input variant="admin" label="Klascode" value={coTeacherCode} onChange={e => setCoTeacherCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleJoinAsCoTeacher();
                    }
                  }} placeholder="Bijv. ABC123" maxLength={8} autoFocus data-id="element-8829" />
                        {coTeacherJoinError && <p className="mt-2 text-xs text-rose-600" data-id="element-8830">
                            {coTeacherJoinError}
                          </p>}
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto mt-2 sm:mt-5" data-id="element-8831">
                        <Button onClick={handleJoinAsCoTeacher} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-8832">
                          Word co-docent
                        </Button>
                        <Button onClick={() => {
                    setIsJoiningAsCoTeacher(false);
                    setCoTeacherCode('');
                    setCoTeacherJoinError(null);
                  }} variant="secondary" data-id="element-8833">
                          <X className="w-4 h-4" data-id="element-8834" />
                        </Button>
                      </div>
                    </div>
                  </div>}
              </div>
            </Card>
          </div>}

        {/* Archive Section */}
        {activeTab === 'archief' && recentArchivedLinks.length > 0 && <Card className="border-slate-200 shadow-sm bg-white overflow-hidden" data-id="element-8835">
            <div className="p-4 border-b border-slate-100 bg-slate-50" data-id="element-8836">
              <div className="flex items-center gap-2" data-id="element-8837">
                <ArchiveRestore className="w-5 h-5 text-slate-500" data-id="element-8838" />
                <h3 className="text-sm font-bold text-slate-900" data-id="element-8839">
                  Onlangs ontkoppeld ({recentArchivedLinks.length})
                </h3>
              </div>
              <p className="text-xs text-slate-500 mt-1" data-id="element-8840">
                Na 30 dagen worden ontkoppelingen automatisch definitief.
              </p>
            </div>

            <div className="divide-y divide-slate-100 bg-white" data-id="element-8841">
              {recentArchivedLinks.map(link => {
            const learner = accounts.accountsById[link.learnerId];
            if (!learner) return null;
            const daysAgo = Math.floor((Date.now() - link.archivedAt) / (1000 * 60 * 60 * 24));
            const timeText = daysAgo === 0 ? 'Vandaag' : daysAgo === 1 ? 'Gisteren' : `${daysAgo} dagen geleden`;
            let contextText = 'uit account';
            if (link.reason === 'unlink' && link.fromClassroomIds.length > 0) {
              const classNames = link.fromClassroomIds.map(id => classrooms.find(c => c.id === id)?.name).filter(Boolean);
              if (classNames.length > 0) {
                contextText = `uit ${classNames.join(', ')}`;
              }
            } else if (link.reason === 'classroom_delete') {
              contextText = 'door klasverwijdering';
            }
            return <div key={link.learnerId} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-id="element-8842">
                    <div data-id="element-8843">
                      <div className="font-bold text-sm text-slate-900" data-id="element-8844">
                        {learner.displayName}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-8845">
                        {timeText} · {contextText}
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-8846">
                      <Button onClick={() => {
                  restoreArchivedLearner(activeAccount.id, link.learnerId, {
                    toClassroomId: link.fromClassroomIds[0]
                  });
                  toast.success(`${learner.displayName} is hersteld.`);
                }} variant="secondary" size="sm" className="text-xs font-bold" data-id="element-8847">
                        Herstel
                      </Button>
                    </div>
                  </div>;
          })}
            </div>
          </Card>}
      </div>

      <AddLearnerSheet open={showAddStudent} onClose={() => setShowAddStudent(false)} activeAccount={activeAccount} classrooms={classrooms} selectedClassroomId={selectedClassroomId} onComplete={() => setShowAddStudent(false)} data-id="element-8848" />

      <CoTeacherWelcomeModal open={!!coTeacherWelcomeFor} classroom={coTeacherWelcomeFor} onClose={() => setCoTeacherWelcomeFor(null)} data-id="element-8849" />

      <TransferClassroomModal open={!!transferClassroomId} classroom={classrooms.find(c => c.id === transferClassroomId) || null} activeAccount={activeAccount} onClose={() => setTransferClassroomId(null)} data-id="element-8850" />

      <ConfirmDialog open={!!regenerateCodeClassroomId} title="Nieuwe code genereren" message="De oude code stopt direct met werken. Leerlingen die hem nog niet gebruikt hebben moeten de nieuwe krijgen." confirmLabel="Ja, genereer nieuwe code" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (regenerateCodeClassroomId) {
        regenerateJoinCode(activeAccount.id, regenerateCodeClassroomId);
        setRegenerateCodeClassroomId(null);
      }
    }} onCancel={() => setRegenerateCodeClassroomId(null)} data-id="element-8851" />

      <ConfirmDialog open={!!deleteClassroomId} title="Klas verwijderen" message={classroomToDelete ? `Weet je zeker dat je "${classroomToDelete.name}" wilt verwijderen?\n\n• ${classroomToDelete.learnerIds.length} leerlingen blijven gekoppeld aan je account.\n• ${classroomToDelete.coTeacherIds?.length || 0} co-docenten verliezen direct toegang.\n• Koppelcode ${classroomToDelete.joinCode} vervalt direct.\n\nJe kunt dit niet ongedaan maken.` : ''} confirmLabel="Ja, verwijder klas" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (deleteClassroomId) {
        removeClassroom(activeAccount.id, deleteClassroomId);
        setDeleteClassroomId(null);
      }
    }} onCancel={() => setDeleteClassroomId(null)} data-id="element-8852" />

      <ConfirmDialog open={!!pendingRemoveStudent} title="Leerling verwijderen uit klas" message={pendingRemoveStudent ? `Weet je zeker dat je ${pendingRemoveStudent.studentName} uit ${pendingRemoveStudent.classroomName} wilt verwijderen? ${pendingRemoveStudent.studentName} blijft wel gekoppeld aan jouw account.` : ''} confirmLabel="Ja, verwijder" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingRemoveStudent) {
        removeLearnerFromClassroom(activeAccount.id, pendingRemoveStudent.classroomId, pendingRemoveStudent.studentId);
        setPendingRemoveStudent(null);
      }
    }} onCancel={() => setPendingRemoveStudent(null)} data-id="element-8853" />

      <ConfirmDialog open={!!pendingUnlinkStudent} title="Koppeling verbreken" message={pendingUnlinkStudent ? `${pendingUnlinkStudent.studentName} wordt verwijderd uit al je klassen en je verliest toegang tot de voortgang. Het leerlingaccount blijft bestaan.` : ''} confirmLabel="Ja, ontkoppel" cancelLabel="Annuleren" variant="danger" onConfirm={() => {
      if (pendingUnlinkStudent) {
        const studentId = pendingUnlinkStudent.studentId;
        const studentName = pendingUnlinkStudent.studentName;
        unlinkTeacherFromLearner(activeAccount.id, studentId);
        setPendingUnlinkStudent(null);
        toast.success(`${studentName} is ontkoppeld`, {
          action: {
            label: 'Ongedaan maken',
            onClick: () => {
              restoreArchivedLearner(activeAccount.id, studentId);
              toast.success(`${studentName} is weer gekoppeld.`);
            }
          },
          duration: 5000
        });
      }
    }} onCancel={() => setPendingUnlinkStudent(null)} data-id="element-8854" />
    </PageLayout>;
}